import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Table,
  Badge,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";

interface UsageAnalyticsData {
  totalMonthlyCost: number;
  totalRequests: number;
  costByTier: { starter: number; pro: number; enterprise: number };
  topTenants: Array<{
    tenantId: string;
    name: string;
    tier: string;
    cost: number;
    requests: number;
    grossMarginUsd?: number | null;
    localUsageShare?: number;
    divergenceRate?: number;
  }>;
  dailyCostTrend: Array<{
    date: string;
    cost: number;
    requests: number;
  }>;
}

interface FleetEconomicsData {
  totalRevenueUsd: number;
  totalFixedInfraCostUsd: number;
  totalPlatformPaidInferenceCogsUsd: number;
  totalReferenceSpendUsd: number;
  totalCogsUsd: number;
  grossMarginUsd: number | null;
  grossMarginPct: number | null;
  localUsageShare: number;
  externalUsageShare: number;
  divergenceRate: number;
  unresolvedTruthRate: number;
  totalRequests: number;
}

interface ModelAnalyticsData {
  windowHours: number;
  totals: {
    totalRequests: number;
    totalCostUsd: number;
    opusRequests: number;
    opusRequestShare: number;
    deployments: number;
    models: number;
  };
  models: Array<{
    provider: string;
    model: string;
    requests: number;
    costUsd: number;
    deploymentCount: number;
    tenantCount: number;
    requestShare: number;
    costShare: number;
    opus: boolean;
  }>;
  deployments: Array<{
    deploymentId: string;
    tenantId: string;
    tenantName: string;
    totalRequests: number;
    totalCostUsd: number;
    opusRequests: number;
    opusShare: number;
    models: Array<{
      provider: string;
      model: string;
      requests: number;
      costUsd: number;
      requestShare: number;
      costShare: number;
      opus: boolean;
    }>;
  }>;
}

const TIER_COLORS: Record<string, string> = {
  starter: "#4CAF50",
  pro: "#1A61ED",
  enterprise: "#9B59B6",
};

const tooltipStyle = {
  backgroundColor: "var(--c-surface)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  fontSize: 12,
};

export default function UsageAnalytics() {
  const { data, isLoading, error } = useQuery<UsageAnalyticsData>({
    queryKey: ["fleet", "usage-analytics"],
    queryFn: () => apiGet<UsageAnalyticsData>("/api/fleet/usage-analytics"),
  });

  const {
    data: modelAnalytics,
    isLoading: modelAnalyticsLoading,
    error: modelAnalyticsError,
  } = useQuery<ModelAnalyticsData>({
    queryKey: ["fleet", "model-analytics"],
    queryFn: () => apiGet<ModelAnalyticsData>("/api/fleet/model-analytics?windowHours=24"),
  });

  const { data: economics } = useQuery<FleetEconomicsData>({
    queryKey: ["fleet", "economics"],
    queryFn: () => apiGet<FleetEconomicsData>("/api/fleet/economics"),
  });

  usePolling(
    [
      ["fleet", "usage-analytics"],
      ["fleet", "economics"],
      ["fleet", "model-analytics"],
    ],
    60_000,
  );

  const pieData = useMemo(() => {
    if (!data?.costByTier) return [];
    return Object.entries(data.costByTier)
      .filter(([, cost]) => cost > 0)
      .map(([tier, cost]) => ({
        name: tier.charAt(0).toUpperCase() + tier.slice(1),
        value: Math.round(cost * 100) / 100,
        tier,
      }));
  }, [data]);

  const sortedTenants = useMemo(() => {
    if (!data?.topTenants) return [];
    return [...data.topTenants].sort((a, b) => b.cost - a.cost);
  }, [data]);

  const topModels = useMemo(() => {
    if (!modelAnalytics?.models) return [];
    return [...modelAnalytics.models].sort((a, b) => b.requests - a.requests);
  }, [modelAnalytics]);

  const deploymentMix = useMemo(() => {
    if (!modelAnalytics?.deployments) return [];
    return [...modelAnalytics.deployments].sort((a, b) => b.totalRequests - a.totalRequests);
  }, [modelAnalytics]);

  if (isLoading && !data) {
    return (
      <div>
        <PageHeader title="Usage Analytics" subtitle="Fleet-wide cost and request tracking" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Usage Analytics" subtitle="Fleet-wide cost and request tracking" />
        <QueryError error={error} queryKeys={[["fleet", "usage-analytics"]]} />
      </div>
    );
  }

  const tenantColumns = [
    {
      key: "name",
      header: "Tenant",
      render: (row: UsageAnalyticsData["topTenants"][0]) => (
        <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
          {row.name}
        </span>
      ),
    },
    {
      key: "tier",
      header: "Tier",
      render: (row: UsageAnalyticsData["topTenants"][0]) => (
        <Badge color={row.tier === "enterprise" ? "purple" : row.tier === "pro" ? "blue" : "green"}>
          {row.tier}
        </Badge>
      ),
    },
    {
      key: "requests",
      header: "Requests",
      render: (row: UsageAnalyticsData["topTenants"][0]) => row.requests.toLocaleString(),
    },
    {
      key: "cost",
      header: "Cost",
      render: (row: UsageAnalyticsData["topTenants"][0]) => (
        <span className="font-mono text-sm" style={{ color: "var(--c-text)" }}>
          ${row.cost.toFixed(2)}
        </span>
      ),
    },
    {
      key: "grossMarginUsd",
      header: "Margin",
      render: (row: UsageAnalyticsData["topTenants"][0]) =>
        row.grossMarginUsd === null || row.grossMarginUsd === undefined ? (
          <span style={{ color: "var(--c-text-faint)" }}>Incomplete</span>
        ) : (
          <span className="font-mono text-sm" style={{ color: "var(--c-text)" }}>
            ${row.grossMarginUsd.toFixed(2)}
          </span>
        ),
    },
    {
      key: "localUsageShare",
      header: "Local Share",
      render: (row: UsageAnalyticsData["topTenants"][0]) => (
        <span style={{ color: "var(--c-text-secondary)" }}>
          {((row.localUsageShare ?? 0) * 100).toFixed(1)}%
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Usage Analytics" subtitle="Fleet-wide cost and request tracking" />

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Monthly Cost" value={"$" + (data?.totalMonthlyCost ?? 0).toFixed(2)} />
        <StatCard title="Total Requests" value={(data?.totalRequests ?? 0).toLocaleString()} />
        <StatCard
          title="Starter Cost"
          value={"$" + (data?.costByTier.starter ?? 0).toFixed(2)}
          color={TIER_COLORS.starter}
        />
        <StatCard
          title="Pro Cost"
          value={"$" + (data?.costByTier.pro ?? 0).toFixed(2)}
          color={TIER_COLORS.pro}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Revenue" value={`$${(economics?.totalRevenueUsd ?? 0).toFixed(2)}`} />
        <StatCard
          title="Gross Margin"
          value={
            economics?.grossMarginUsd === null || economics?.grossMarginUsd === undefined
              ? "Incomplete"
              : `$${economics.grossMarginUsd.toFixed(2)}`
          }
        />
        <StatCard
          title="Local Usage Share"
          value={`${((economics?.localUsageShare ?? 0) * 100).toFixed(1)}%`}
        />
        <StatCard
          title="Truth Divergence"
          value={`${((economics?.divergenceRate ?? 0) * 100).toFixed(1)}%`}
          color={(economics?.divergenceRate ?? 0) <= 0.05 ? "#4CAF50" : "#E53935"}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Model Requests (24h)"
          value={(modelAnalytics?.totals.totalRequests ?? 0).toLocaleString()}
        />
        <StatCard
          title="Opus Share (24h)"
          value={`${((modelAnalytics?.totals.opusRequestShare ?? 0) * 100).toFixed(1)}%`}
          color={(modelAnalytics?.totals.opusRequestShare ?? 0) <= 0.1 ? "#4CAF50" : "#E53935"}
        />
        <StatCard
          title="Tracked Models"
          value={(modelAnalytics?.totals.models ?? 0).toLocaleString()}
        />
        <StatCard
          title="Tracked Deployments"
          value={(modelAnalytics?.totals.deployments ?? 0).toLocaleString()}
        />
      </div>

      {/* Daily cost trend */}
      <SectionHeader title="Daily Cost Trend" subtitle="Inference costs over the last 30 days" />
      <Card className="mb-8">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data?.dailyCostTrend ?? []}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: "var(--c-text-secondary)" }}
              tickFormatter={(v: string) => v.slice(5)} // "MM-DD"
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--c-text-secondary)" }}
              tickFormatter={(v: number) => `$${v}`}
            />
            <RechartsTooltip
              contentStyle={tooltipStyle}
              formatter={(value: number, name: string) => [
                name === "cost" ? `$${value.toFixed(2)}` : value.toLocaleString(),
                name === "cost" ? "Cost" : "Requests",
              ]}
            />
            <Bar dataKey="cost" fill="#1A61ED" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Cost by tier pie */}
      <SectionHeader
        title="Cost by Tier"
        subtitle="How costs are distributed across subscription tiers"
      />
      <Card className="mb-8">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              outerRadius={90}
              innerRadius={45}
              dataKey="value"
              nameKey="name"
              label={({ name, percent }: { name: string; percent: number }) =>
                `${name} (${(percent * 100).toFixed(0)}%)`
              }
              labelLine={{ stroke: "var(--c-text-faint)" }}
            >
              {pieData.map((entry) => (
                <Cell key={entry.tier} fill={TIER_COLORS[entry.tier] ?? "#888"} />
              ))}
            </Pie>
            <RechartsTooltip
              contentStyle={tooltipStyle}
              formatter={(value: number) => [`$${value.toFixed(2)}`, "Cost"]}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: "var(--c-text-secondary)" }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      {/* Top consumers table */}
      <SectionHeader title="Top Consumers" subtitle="Tenants ranked by inference cost this month" />
      <Card padding={false}>
        <Table
          columns={tenantColumns}
          data={sortedTenants as unknown as Record<string, unknown>[]}
          keyField="tenantId"
          emptyMessage="No usage data"
        />
      </Card>

      <SectionHeader
        title="Top Models (24h)"
        subtitle="Model mix and Opus usage across deployments"
      />
      <Card padding={false} className="mb-8">
        {modelAnalyticsLoading && !modelAnalytics ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={20} />
          </div>
        ) : modelAnalyticsError && !modelAnalytics ? (
          <div className="p-4">
            <QueryError error={modelAnalyticsError} queryKeys={[["fleet", "model-analytics"]]} />
          </div>
        ) : (
          <Table
            columns={[
              {
                key: "model",
                header: "Model",
                render: (row: ModelAnalyticsData["models"][0]) => (
                  <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
                    {row.model}
                  </span>
                ),
              },
              {
                key: "provider",
                header: "Provider",
                render: (row: ModelAnalyticsData["models"][0]) => (
                  <span className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
                    {row.provider}
                  </span>
                ),
              },
              {
                key: "requests",
                header: "Requests",
                render: (row: ModelAnalyticsData["models"][0]) => row.requests.toLocaleString(),
              },
              {
                key: "requestShare",
                header: "Share",
                render: (row: ModelAnalyticsData["models"][0]) =>
                  `${(row.requestShare * 100).toFixed(1)}%`,
              },
              {
                key: "costUsd",
                header: "Cost",
                render: (row: ModelAnalyticsData["models"][0]) => `$${row.costUsd.toFixed(2)}`,
              },
              {
                key: "opus",
                header: "Tier",
                render: (row: ModelAnalyticsData["models"][0]) => (
                  <Badge color={row.opus ? "red" : "green"}>{row.opus ? "Opus" : "Standard"}</Badge>
                ),
              },
            ]}
            data={topModels as unknown as Record<string, unknown>[]}
            keyField="model"
            emptyMessage="No model analytics in the selected window"
          />
        )}
      </Card>

      <SectionHeader
        title="Deployment Model Mix (24h)"
        subtitle="Per-deployment request volume and Opus share"
      />
      <Card padding={false}>
        <Table
          columns={[
            {
              key: "tenantName",
              header: "Tenant",
              render: (row: ModelAnalyticsData["deployments"][0]) => (
                <span className="font-medium text-sm" style={{ color: "var(--c-text)" }}>
                  {row.tenantName}
                </span>
              ),
            },
            {
              key: "deploymentId",
              header: "Deployment",
              render: (row: ModelAnalyticsData["deployments"][0]) => (
                <span className="font-mono text-xs" style={{ color: "var(--c-text-secondary)" }}>
                  {row.deploymentId}
                </span>
              ),
            },
            {
              key: "totalRequests",
              header: "Requests",
              render: (row: ModelAnalyticsData["deployments"][0]) =>
                row.totalRequests.toLocaleString(),
            },
            {
              key: "opusShare",
              header: "Opus Share",
              render: (row: ModelAnalyticsData["deployments"][0]) =>
                `${(row.opusShare * 100).toFixed(1)}%`,
            },
            {
              key: "totalCostUsd",
              header: "Cost",
              render: (row: ModelAnalyticsData["deployments"][0]) =>
                `$${row.totalCostUsd.toFixed(2)}`,
            },
          ]}
          data={deploymentMix as unknown as Record<string, unknown>[]}
          keyField="deploymentId"
          emptyMessage="No deployment-level model analytics in the selected window"
        />
      </Card>
    </div>
  );
}
